/**
 * GlobalCitationTooltip Component
 *
 * A single tooltip instance rendered at root level that reads state from context.
 * This prevents flickering during streaming because the tooltip never unmounts.
 *
 * Place this component once at a high level (e.g., inside CitationTooltipProvider)
 * and it will display tooltip content for any CitationBubble that dispatches hover events.
 *
 * Two renderings: Claude's citations keep the original tooltip; citations spliced
 * in from Gemini's grounding get a site-first one, where a merged chip's sources
 * are paged, each with its own link, with the pager on the first row.
 */

import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { WebCitationTooltipData } from "../../contexts/CitationTooltipContext";
import { useCitationTooltip } from "../../contexts/CitationTooltipContext";
import { CitationTooltipPager, CitationTooltipPages } from "./CitationGroupControls";
import { useTooltipPosition } from "./hooks/useTooltipPosition";
import { usePinnedTooltipDismiss } from "./hooks/usePinnedTooltipDismiss";
import { SiteFavicon } from "./SiteFavicon";
import { sourceSiteDomain } from "../../utils/webSources";

/**
 * Extract domain from URL for favicon lookup
 */
function extractDomain(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch {
    return "";
  }
}

/**
 * Get Google Favicon service URL
 */
function getFaviconUrl(url: string): string {
  const domain = extractDomain(url);
  if (!domain) return "";
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
}

/**
 * The tooltip for Claude's own web citations, unchanged.
 * Renders via portal for proper stacking.
 */
const ClassicCitationTooltip: React.FC = () => {
  const { state, handleTooltipMouseEnter, handleTooltipMouseLeave } = useCitationTooltip();
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const { isVisible, anchorRect, source, url, title } = state;

  // Calculate position based on anchor rect from context
  useEffect(() => {
    if (!isVisible || !anchorRect || !tooltipRef.current) return;

    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    // Position above the bubble by default
    let top = anchorRect.top - tooltipRect.height - 8;
    let left = anchorRect.left + anchorRect.width / 2 - tooltipRect.width / 2;

    // Keep within viewport bounds
    const padding = 8;

    // If tooltip goes above viewport, show below
    if (top < padding) {
      top = anchorRect.bottom + 8;
    }

    // Keep within horizontal bounds
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }

    setPosition({ top, left });
  }, [isVisible, anchorRect]);

  // Don't render anything if not visible
  if (!isVisible) {
    return null;
  }

  const faviconUrl = url ? getFaviconUrl(url) : "";
  const domain = url ? extractDomain(url) : "";

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="citation-tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
        maxWidth: "320px",
        padding: "12px",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        fontSize: "13px",
        lineHeight: "1.4",
        color: "#212529",
        pointerEvents: "auto",
      }}
    >
      {/* Article title (from web search results) or source name as fallback */}
      <div
        style={{
          fontWeight: 500,
          marginBottom: "8px",
          color: "#212529",
          wordBreak: "break-word",
        }}
      >
        {title ?? source}
      </div>

      {/* Domain with favicon */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          fontSize: "12px",
          color: "#6C757D",
        }}
      >
        {faviconUrl && (
          <img
            src={faviconUrl}
            alt=""
            width={16}
            height={16}
            style={{
              borderRadius: "2px",
              flexShrink: 0,
            }}
            onError={(e) => {
              // Hide broken favicon images
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {domain}
        </span>
      </div>
    </div>
  );

  // Render via portal to document body
  return createPortal(tooltipContent, document.body);
};

const openSource = (url: string | undefined) => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * The article title (or, without one, the source name) when it says more than
 * the site does. Gemini's sources carry only the domain, which would otherwise
 * be shown twice; Claude's carry the article title.
 */
const pageTitle = (item: WebCitationTooltipData, domain: string): string | null => {
  const candidates = [item.title?.trim(), item.source.trim()];
  // The first non-empty one: an empty title falls back to the source name too.
  const title = candidates.find((candidate) => !!candidate);

  // "example.com" over "www.example.com" says nothing new either.
  return title && title !== domain && title !== domain.replace(/^www\./, "") ? title : null;
};

/** Text that opens the source when `linked`; plain text otherwise. */
const SourceLink: React.FC<{
  url: string | undefined;
  linked: boolean;
  style?: React.CSSProperties;
  children: React.ReactNode;
}> = ({ url, linked, style, children }) => {
  const isLink = linked && !!url;

  return (
    <span
      role={isLink ? "link" : undefined}
      tabIndex={isLink ? 0 : undefined}
      onClick={isLink ? () => openSource(url) : undefined}
      onKeyDown={
        isLink
          ? (e) => {
              if (e.key === "Enter") openSource(url);
            }
          : undefined
      }
      style={{
        color: isLink ? "#0F6CBD" : undefined,
        cursor: isLink ? "pointer" : "default",
        ...style,
      }}
    >
      {children}
    </span>
  );
};

/** Favicon and domain on one line. */
const SiteRow: React.FC<{
  domain: string;
  url: string | undefined;
  linked: boolean;
  emphasised: boolean;
}> = ({ domain, url, linked, emphasised }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "6px",
      minWidth: 0,
      fontSize: emphasised ? "13px" : "12px",
      fontWeight: emphasised ? 500 : 400,
      color: emphasised ? "#212529" : "#6C757D",
    }}
  >
    <SiteFavicon domain={domain} />
    <SourceLink
      url={url}
      linked={linked}
      style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
    >
      {domain}
    </SourceLink>
  </div>
);

/** The first line of a source: its page title, or its site when it has none. */
const SourceHeading: React.FC<{ item: WebCitationTooltipData; linked: boolean }> = ({
  item,
  linked,
}) => {
  const domain = sourceSiteDomain(item.url, item.source);
  const title = pageTitle(item, domain);

  if (!title) {
    return <SiteRow domain={domain || item.source} url={item.url} linked={linked} emphasised />;
  }

  return (
    <div style={{ fontWeight: 500, color: "#212529", wordBreak: "break-word" }}>
      <SourceLink url={item.url} linked={linked}>
        {title}
      </SourceLink>
    </div>
  );
};

/** The site under a page title; nothing when the heading already is the site. */
const SourceSite: React.FC<{ item: WebCitationTooltipData }> = ({ item }) => {
  const domain = sourceSiteDomain(item.url, item.source);

  if (!pageTitle(item, domain)) return null;

  return (
    <div style={{ marginTop: "8px" }}>
      <SiteRow domain={domain} url={item.url} linked={false} emphasised={false} />
    </div>
  );
};

/**
 * The tooltip for citations spliced in from Gemini's grounding.
 * Renders via portal for proper stacking.
 */
const GroundedCitationTooltip: React.FC = () => {
  const { state, handleTooltipMouseEnter, handleTooltipMouseLeave, pageTooltip, closeTooltip } =
    useCitationTooltip();
  const { isVisible, anchorRect, source, url, title, group, pinned } = state;
  const { ref: tooltipRef, position } = useTooltipPosition<HTMLDivElement>(anchorRect, isVisible);

  usePinnedTooltipDismiss(isVisible && pinned, tooltipRef, group?.anchorEl ?? null, closeTooltip);

  // Don't render anything if not visible
  if (!isVisible) {
    return null;
  }

  // A merged chip's sources, paged; null for a single citation
  const pages = group && group.items.length > 1 ? group : null;
  const single: WebCitationTooltipData = { source, url, title };

  const tooltipContent = (
    <div
      ref={tooltipRef}
      className="citation-tooltip"
      onMouseEnter={handleTooltipMouseEnter}
      onMouseLeave={handleTooltipMouseLeave}
      role="tooltip"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000,
        maxWidth: "320px",
        maxHeight: `${position.maxHeight}px`,
        overflowY: "auto",
        padding: "12px",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
        fontSize: "13px",
        lineHeight: "1.4",
        color: "#212529",
        pointerEvents: "auto",
      }}
    >
      {pages ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <CitationTooltipPages index={pages.index} style={{ flex: 1, minWidth: 0 }}>
              {pages.items.map((item, i) => (
                <SourceHeading key={i} item={item} linked />
              ))}
            </CitationTooltipPages>
            <CitationTooltipPager
              index={pages.index}
              total={pages.items.length}
              onPage={pageTooltip}
            />
          </div>
          <CitationTooltipPages index={pages.index}>
            {pages.items.map((item, i) => (
              <SourceSite key={i} item={item} />
            ))}
          </CitationTooltipPages>
        </>
      ) : (
        <>
          <SourceHeading item={single} linked={false} />
          <SourceSite item={single} />
        </>
      )}
    </div>
  );

  // Render via portal to document body
  return createPortal(tooltipContent, document.body);
};

/** Picks the rendering for the citation being shown. */
export const GlobalCitationTooltip: React.FC = () => {
  const { state } = useCitationTooltip();

  return state.grounded ? <GroundedCitationTooltip /> : <ClassicCitationTooltip />;
};

export default GlobalCitationTooltip;
